import { useQuery } from "@tanstack/react-query";
import type { AxiosRequestConfig } from "axios";
import { unauthHttpRequest } from "@fmc-digital/auth-api";
import type { DefaultObject } from "@fmc-digital/auth-api";
import { useServerTime } from "./useServerTime";
import { checkMaintenanceWindow } from "../utils/maintenanceTimeUtils";
import { useTokenReadiness } from "./useTokenReadiness";

interface MaintenanceBannerData {
  showBanner: boolean;
  outage: boolean;
  message: string;
  startDateTime: string | null;
  endDateTime: string | null;
  // Computed fields based on server time
  isInMaintenanceWindow: boolean;
  isInOutageWindow: boolean;
  shouldShowBanner: boolean;
}

/**
 * Fetch maintenance banner API using baseUrlt1 (alternative base URL)
 */
const fetchMaintenanceBanner = async (
  signal?: AbortSignal
): Promise<DefaultObject> => {
  const config: AxiosRequestConfig = {
    url: `/maintenance-banner`,
    method: "GET",
    signal,
    headers: {
      'X-Use-BaseUrl': '1', // Use baseUrlt1 instead of default baseUrl
    },
  };

  return unauthHttpRequest<DefaultObject>(config);
};

/**
 * Hook to fetch maintenance banner data using baseUrlt1
 * 
 * Follows servicing-login pattern:
 * 1. Fetch server time to avoid client clock skew
 * 2. Fetch maintenance banner data with time windows
 * 3. Check if current time is in maintenance or outage window
 * 4. Display appropriate banner message
 * 
 * @param enabled - Whether to enable the query (default: true)
 * @returns Maintenance banner status and message
 */
export function useMaintenanceBanner(enabled: boolean = true) {
  const { isTokenReady, hasBaseUrlt1 } = useTokenReadiness();

  // Fetch server time (following servicing-login pattern)
  const { serverTime, hasServerTime } = useServerTime(enabled && hasBaseUrlt1);

  const { data, isLoading, error } = useQuery({
    queryKey: ["maintenance-banner"],
    queryFn: ({ signal }) => fetchMaintenanceBanner(signal),
    enabled: enabled && hasBaseUrlt1 && isTokenReady, // Only enable when token is ready
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 1,
    // Prevent refetching on window focus since this is static content
    refetchOnWindowFocus: false,
  });

  // Parse the response and check maintenance windows using server time
  const maintenanceBannerData: MaintenanceBannerData = (() => {
    const showBanner = (data?.showBanner as boolean) ?? false;
    const outage = (data?.outage as boolean) ?? false;
    const message = (data?.bannerMessage as string) ?? "";
    const startDateTime = (data?.startDateTime as string) ?? null;
    const endDateTime = (data?.endDateTime as string) ?? null;

    // Check if we're in maintenance or outage window based on server time
    const windowCheck = checkMaintenanceWindow(serverTime, startDateTime, endDateTime);

    // Combine API flag with time-based check
    // Show banner if API says so AND we're in the time window
    const shouldShowBanner = showBanner && windowCheck.shouldShowBanner;
    
    // Determine if this is truly an outage based on time window
    const isActuallyOutage = outage && windowCheck.isInOutageWindow;

    return {
      showBanner,
      outage,
      message,
      startDateTime,
      endDateTime,
      isInMaintenanceWindow: windowCheck.isInMaintenanceWindow,
      isInOutageWindow: windowCheck.isInOutageWindow,
      shouldShowBanner,
      isActuallyOutage,
    } as MaintenanceBannerData & { isActuallyOutage: boolean };
  })();

  return {
    showBanner: maintenanceBannerData.showBanner,
    outage: (maintenanceBannerData as any).isActuallyOutage, // Use time-based outage detection
    message: maintenanceBannerData.message,
    startDateTime: maintenanceBannerData.startDateTime,
    endDateTime: maintenanceBannerData.endDateTime,
    isInMaintenanceWindow: maintenanceBannerData.isInMaintenanceWindow,
    isInOutageWindow: maintenanceBannerData.isInOutageWindow,
    shouldShowBanner: maintenanceBannerData.shouldShowBanner,
    serverTime,
    hasServerTime,
    isLoading,
    error,
  };
}
